// src/services/passportService.js - MINIMAL FIX: Only added URL-safe token handling
import { createClient } from '@supabase/supabase-js';
import { PASSPORT_CONFIG, isFeatureEnabled, getTierConfig } from '../config/passportConfig';

class PassportService {
  constructor() {
    this.supabase = createClient(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_ANON_KEY
    );
  }

  /**
 * 🆕 NEW: Get email signup data including member_status and subscribed_at
 */
    async getEmailSignupData(email) {
        try {
        console.log('🎯 Fetching email signup data for:', email);
        
        const { data, error } = await this.supabase
            .from('email_signups')
            .select(`
            email,
            member_status,
            subscribed_at,
            status,
            source,
            metadata
            `)
            .eq('email', email.toLowerCase().trim())
            .eq('status', 'active')
            .single();
    
        if (error) {
            console.error('❌ Error fetching email signup data:', error);
            return { success: false, error: error.message };
        }
    
        console.log('✅ Email signup data fetched:', {
            email: data.email,
            member_status: data.member_status,
            subscribed_at: data.subscribed_at,
            source: data.source
        });
        
        return { success: true, data };
        
        } catch (error) {
        console.error('💥 Exception fetching email signup data:', error);
        return { success: false, error: error.message };
        }
    }

    // 🔥 ENHANCE: Update getPassportSummary to include member_status and subscribed_at
    /**
     * Get simplified passport summary for a user - ENHANCED with member_status
     */
async getPassportSummary(email) {
    try {
      if (!PASSPORT_CONFIG.enabled) {
        return { success: false, error: 'Passport system disabled' };
      }
  
      console.log('🎯 Fetching passport summary for:', email);
      
      // 🆕 NEW: Get both passport data AND email signup data
      const [passportResult, emailResult] = await Promise.all([
        // Original passport RPC call
        this.supabase.rpc('get_passport_summary', {
          p_email: email.toLowerCase().trim()
        }),
        // New email signup data call
        this.getEmailSignupData(email)
      ]);
  
      if (passportResult.error) {
        console.error('❌ Error fetching passport summary:', passportResult.error);
        return { success: false, error: passportResult.error.message };
      }
  
      // 🔥 CRITICAL: Merge passport data with email signup data
      let combinedData = passportResult.data || {};
      
      if (emailResult.success && emailResult.data) {
        combinedData = {
          ...combinedData,
          // 🆕 ADD: Member status and signup date from email_signups table
          member_status: emailResult.data.member_status,
          subscribed_at: emailResult.data.subscribed_at,
          signup_source: emailResult.data.source,
          email: emailResult.data.email
        };
      }
  
      // Filter data based on feature flags
      const filteredData = this.filterPassportData(combinedData);
      
      console.log('✅ Enhanced passport summary (with member_status):', {
        ...filteredData,
        member_status: combinedData.member_status,
        subscribed_at: combinedData.subscribed_at
      });
      
      return { success: true, data: filteredData };
      
    } catch (error) {
      console.error('💥 Exception fetching passport summary:', error);
      return { success: false, error: error.message };
    }
  }


  /**
   * 🆕 NEW: Get detailed passport data for member passport page
   */
  async getDetailedPassportData(email) {
    try {
      if (!PASSPORT_CONFIG.enabled) {
        return { success: false, error: 'Passport system disabled' };
      }

      console.log('🎯 Fetching detailed passport data for:', email);
      
      const { data, error } = await this.supabase.rpc('get_detailed_passport_data', {
        p_email: email.toLowerCase().trim()
      });

      if (error) {
        console.error('❌ Error fetching detailed passport data:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Detailed passport data fetched:', data);
      return { success: true, data: data };
      
    } catch (error) {
      console.error('💥 Exception fetching detailed passport data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🆕 NEW: Get event attendance history for member passport page
   */
  async getEventHistory(email) {
    try {
      if (!PASSPORT_CONFIG.enabled) {
        return { success: false, error: 'Passport system disabled' };
      }

      console.log('🎯 Fetching event history for:', email);
      
      const { data, error } = await this.supabase.rpc('get_member_event_history', {
        p_email: email.toLowerCase().trim()
      });

      if (error) {
        console.error('❌ Error fetching event history:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Event history fetched:', data);
      return { success: true, data: data || [] };
      
    } catch (error) {
      console.error('💥 Exception fetching event history:', error);
      return { success: false, error: error.message };
    }
  }

/*
* 11-7-25
*/

// Add this section after the existing secure methods (around line 350, after getEventHistorySecure)

/**
 * 🆕 NEW: Get upcoming events (only after magic link validation)
 */
/**
 * 🆕 NEW: Get upcoming events (only after magic link validation)
 * 🔧 FIXED: Removed X-Passport-Session header causing CORS issues
 */
async getUpcomingEventsSecure(email) {
  try {
    // Verify session is authenticated
    if (!this.isSessionAuthenticated(email)) {
      console.error('❌ Session not authenticated');
      return { 
        success: false, 
        error: 'Session not authenticated. Please use the magic link again.' 
      };
    }

    console.log('📅 [DEBUG] Fetching upcoming events for:', email.substring(0, 10) + '...');
    console.log('📅 [DEBUG] Calling Edge Function:', `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/passport-details`);
    
    // Call the passport-details Edge Function (which now includes upcoming events)
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/passport-details`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          // 🔧 REMOVED: X-Passport-Session header - not needed and causes CORS issues
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim()
        })
      }
    );

    console.log('📅 [DEBUG] Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ [DEBUG] Failed to fetch upcoming events:', errorData);
      return { 
        success: false, 
        error: errorData.error || 'Failed to fetch upcoming events',
        data: [] // Fail gracefully
      };
    }

    const result = await response.json();
    console.log('📅 [DEBUG] Full response:', result);
    console.log('📅 [DEBUG] Upcoming events data:', result.data?.upcomingEvents);
    
    if (result.success && result.data?.upcomingEvents !== undefined) {
      console.log(`✅ [DEBUG] Found ${result.data.upcomingEvents.length} upcoming events`);
      console.log('📅 [DEBUG] Events:', result.data.upcomingEvents);
      return { 
        success: true, 
        data: result.data.upcomingEvents 
      };
    } else {
      console.log('⚠️ [DEBUG] No upcomingEvents field in response');
      return { 
        success: true, 
        data: [] 
      };
    }
    
  } catch (error) {
    console.error('💥 [DEBUG] Exception fetching upcoming events:', error);
    return { 
      success: false, 
      error: 'Failed to fetch upcoming events',
      data: [] // Fail gracefully with empty array
    };
  }
}



/**
 * 🆕 NEW: Format upcoming event for display
 */
formatUpcomingEvent(event) {
  if (!event) return null;

  const eventDate = new Date(event.event_date);
  const daysUntil = Math.ceil(event.days_until_event);
  
  return {
    id: event.id,
    slug: event.event_slug,
    title: event.event_title,
    type: event.event_type,
    date: eventDate,
    formattedDate: eventDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }),
    formattedTime: eventDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    }),
    daysUntil: daysUntil,
    daysUntilText: this.formatDaysUntil(daysUntil),
    rsvpStatus: event.rsvp_status,
    isPriorityAccess: event.priority_access,
    icon: event.icon || '📅',
    isToday: daysUntil === 0,
    isTomorrow: daysUntil === 1,
    isThisWeek: daysUntil > 0 && daysUntil <= 7
  };
}

/**
 * 🆕 NEW: Format "days until" text
 */
formatDaysUntil(days) {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days > 1 && days <= 7) return `In ${days} days`;
  if (days > 7 && days <= 14) return `In ${Math.ceil(days / 7)} week`;
  if (days > 14 && days <= 30) return `In ${Math.ceil(days / 7)} weeks`;
  return `In ${Math.ceil(days / 30)} month${Math.ceil(days / 30) > 1 ? 's' : ''}`;
}

/**
 * 🆕 NEW: Get upcoming events count by timeframe
 */
categorizeUpcomingEvents(events) {
  if (!events || events.length === 0) {
    return {
      today: [],
      tomorrow: [],
      thisWeek: [],
      thisMonth: [],
      later: [],
      total: 0
    };
  }

  const categorized = {
    today: [],
    tomorrow: [],
    thisWeek: [],
    thisMonth: [],
    later: [],
    total: events.length
  };

  events.forEach(event => {
    const daysUntil = Math.ceil(event.days_until_event);
    
    if (daysUntil === 0) {
      categorized.today.push(event);
    } else if (daysUntil === 1) {
      categorized.tomorrow.push(event);
    } else if (daysUntil <= 7) {
      categorized.thisWeek.push(event);
    } else if (daysUntil <= 30) {
      categorized.thisMonth.push(event);
    } else {
      categorized.later.push(event);
    }
  });

  return categorized;
}

/**
 * 🆕 NEW: Get urgent events (today + tomorrow)
 */
getUrgentEvents(events) {
  if (!events || events.length === 0) return [];
  
  return events.filter(event => {
    const daysUntil = Math.ceil(event.days_until_event);
    return daysUntil <= 1; // Today or tomorrow
  });
}

  /*
  * end of new stuff
  * 11-7-25
  */

  /**
   * 🆕 NEW: Get achievements data for member passport page
   */
  async getAchievements(email) {
    try {
      if (!PASSPORT_CONFIG.enabled) {
        return { success: false, error: 'Passport system disabled' };
      }

      console.log('🎯 Fetching achievements for:', email);
      
      const { data, error } = await this.supabase.rpc('get_member_achievements', {
        p_email: email.toLowerCase().trim()
      });

      if (error) {
        console.error('❌ Error fetching achievements:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Achievements fetched:', data);
      return { success: true, data: data || [] };
      
    } catch (error) {
      console.error('💥 Exception fetching achievements:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🆕 NEW: Get member statistics for passport page overview
   */
  async getMemberStats(email) {
    try {
      if (!PASSPORT_CONFIG.enabled) {
        return { success: false, error: 'Passport system disabled' };
      }

      console.log('🎯 Fetching member stats for:', email);
      
      // Get event count and other stats
      const { data: eventData, error: eventError } = await this.supabase
        .from('event_attendances')
        .select('event_date, location, event_type, created_at')
        .eq('email', email.toLowerCase().trim())
        .order('event_date', { ascending: false });

      if (eventError) {
        console.error('❌ Error fetching member stats:', eventError);
        return { success: false, error: eventError.message };
      }

      // Calculate stats
      const stats = {
        totalEvents: eventData.length,
        totalCities: [...new Set(eventData.map(e => e.location))].length,
        firstEventDate: eventData.length > 0 ? eventData[eventData.length - 1].event_date : null,
        lastEventDate: eventData.length > 0 ? eventData[0].event_date : null,
        favoriteEventType: this.calculateFavoriteEventType(eventData),
        eventsThisMonth: this.countEventsThisMonth(eventData),
        eventsThisYear: this.countEventsThisYear(eventData),
        memberSince: eventData.length > 0 ? eventData[eventData.length - 1].created_at : null
      };

      console.log('✅ Member stats calculated:', stats);
      return { success: true, data: stats };
      
    } catch (error) {
      console.error('💥 Exception fetching member stats:', error);
      return { success: false, error: error.message };
    }
  }

  // 🔐 ===== MAGIC LINK METHODS =====

  /**
   * 🔐 SECURE: Send magic link for passport access
   */
    async sendMagicLink(email) {
        try {
          console.log('🔐 Sending magic link to:', email.substring(0, 10) + '...');
          
          // Call the Edge Function with action='send'
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-passport-magic-link`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
              },
              body: JSON.stringify({
                action: 'send',
                email: email.toLowerCase().trim()
              })
            }
          );
    
          if (!response.ok) {
            const errorData = await response.json();
            console.error('❌ Magic link request failed:', errorData);
            return { 
              success: false, 
              error: errorData.error || 'Failed to send secure link' 
            };
          }
    
          const result = await response.json();
          console.log('✅ Magic link sent successfully');
          
          return { 
            success: true, 
            data: { 
              email: email,
              sent: true,
              expiresIn: 900 // 15 minutes
            } 
          };
          
        } catch (error) {
          console.error('💥 Exception sending magic link:', error);
          return { 
            success: false, 
            error: 'Failed to send secure link. Please try again.' 
          };
        }
      }

  /**
   * 🔐 SECURE: Validate magic link token
   * 🔧 MINIMAL FIX: Added URL-safe token handling
   */
  async validateMagicLinkToken(token) {
    try {
      console.log('🔐 Validating magic link token...');
      
      if (!token) {
        return { success: false, error: 'No security token provided' };
      }

      // 🔧 MINIMAL FIX: Clean token from email client issues
      const cleanedToken = token
        .replace(/\s+/g, '') // Remove whitespace
        .replace(/[<>"']/g, '') // Remove quotes/brackets
        .trim();

      console.log('🔧 Token cleaning:', {
        original: token.substring(0, 20) + '...',
        cleaned: cleanedToken.substring(0, 20) + '...',
        lengthChanged: token.length !== cleanedToken.length
      });

      // Call the Edge Function with action='validate'
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-passport-magic-link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            action: 'validate',
            token: cleanedToken
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Token validation failed:', errorData);
        return { 
          success: false, 
          error: errorData.error || 'Invalid or expired security token' 
        };
      }

      const result = await response.json();
      
      if (result.success && result.data?.email) {
        console.log('✅ Token validated successfully for:', result.data.email.substring(0, 10) + '...');
        
        // Store validated session
        sessionStorage.setItem('passport_authenticated', 'true');
        sessionStorage.setItem('passport_email', result.data.email);
        sessionStorage.setItem('passport_auth_time', Date.now().toString());
        
        return { 
          success: true, 
          data: { 
            email: result.data.email,
            validatedAt: new Date().toISOString()
          } 
        };
      } else {
        return { 
          success: false, 
          error: 'Invalid security token' 
        };
      }
      
    } catch (error) {
      console.error('💥 Exception validating token:', error);
      return { 
        success: false, 
        error: 'Failed to validate security token' 
      };
    }
  }

  /**
   * 🔐 SECURE: Get passport summary (only after magic link validation)
   */
  async getPassportSummarySecure(email) {
    try {
      // Verify session is authenticated
      if (!this.isSessionAuthenticated(email)) {
        return { 
          success: false, 
          error: 'Session not authenticated. Please use the magic link again.' 
        };
      }

      console.log('📊 Fetching secure passport data for:', email.substring(0, 10) + '...');
      
      // Call the Edge Function with action='get-passport-data'
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-passport-magic-link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Passport-Session': sessionStorage.getItem('passport_auth_time') || ''
          },
          body: JSON.stringify({
            action: 'get-passport-data',
            email: email.toLowerCase().trim(),
            sessionToken: sessionStorage.getItem('passport_auth_time')
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Secure passport data request failed:', errorData);
        return { 
          success: false, 
          error: errorData.error || 'Failed to fetch passport data' 
        };
      }

      const result = await response.json();
      console.log('✅ Secure passport data fetched successfully');
      
      return result;
      
    } catch (error) {
      console.error('💥 Exception fetching secure passport data:', error);
      return { 
        success: false, 
        error: 'Failed to fetch passport data' 
      };
    }
  }

  /**
   * 🔐 SECURE: Get event history (only after magic link validation)
   */
  async getEventHistorySecure(email) {
    try {
      // Verify session is authenticated
      if (!this.isSessionAuthenticated(email)) {
        return { 
          success: false, 
          error: 'Session not authenticated. Please use the magic link again.' 
        };
      }

      console.log('📅 Fetching secure event history for:', email.substring(0, 10) + '...');
      
      // Call the Edge Function with action='get-event-history'
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-passport-magic-link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Passport-Session': sessionStorage.getItem('passport_auth_time') || ''
          },
          body: JSON.stringify({
            action: 'get-event-history',
            email: email.toLowerCase().trim(),
            sessionToken: sessionStorage.getItem('passport_auth_time')
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Secure event history request failed:', errorData);
        return { 
          success: false, 
          error: errorData.error || 'Failed to fetch event history' 
        };
      }

      const result = await response.json();
      console.log('✅ Secure event history fetched successfully');
      
      return result;
      
    } catch (error) {
      console.error('💥 Exception fetching secure event history:', error);
      return { 
        success: false, 
        error: 'Failed to fetch event history' 
      };
    }
  }

  /**
   * 🔐 HELPER: Check if current session is authenticated for given email
   */
  isSessionAuthenticated(email) {
    const authFlag = sessionStorage.getItem('passport_authenticated');
    const sessionEmail = sessionStorage.getItem('passport_email');
    const authTime = sessionStorage.getItem('passport_auth_time');
    
    if (!authFlag || !sessionEmail || !authTime) {
      console.log('❌ Session authentication failed: missing session data');
      return false;
    }
    
    if (sessionEmail.toLowerCase() !== email.toLowerCase()) {
      console.log('❌ Session authentication failed: email mismatch');
      return false;
    }
    
    // Check if session is not too old (1 hour max)
    const authAge = Date.now() - parseInt(authTime);
    const maxAge = 60 * 60 * 1000; // 1 hour
    
    if (authAge > maxAge) {
      console.log('❌ Session authentication failed: session expired');
      this.clearSession();
      return false;
    }
    
    console.log('✅ Session authenticated for:', email.substring(0, 10) + '...');
    return true;
  }

  /**
   * 🔐 HELPER: Clear authentication session
   */
  clearSession() {
    sessionStorage.removeItem('passport_authenticated');
    sessionStorage.removeItem('passport_email');
    sessionStorage.removeItem('passport_auth_time');
    console.log('🗑️ Authentication session cleared');
  }

  /**
   * 🔐 HELPER: Get session info for debugging
   */
  getSessionInfo() {
    return {
      authenticated: sessionStorage.getItem('passport_authenticated') === 'true',
      email: sessionStorage.getItem('passport_email'),
      authTime: sessionStorage.getItem('passport_auth_time'),
      isValid: this.isSessionAuthenticated(sessionStorage.getItem('passport_email') || '')
    };
  }

  // 🔐 ===== END MAGIC LINK METHODS =====

  /**
   * 🆕 NEW: Process passport data from QR check-in response
   */
  processCheckinPassportData(checkinResponse) {
    try {
      if (!PASSPORT_CONFIG.enabled || !checkinResponse?.data?.passport) {
        return null;
      }

      const passportData = checkinResponse.data.passport;
      
      // Filter based on feature flags
      const processedData = this.filterPassportData(passportData);
      
      // Add check-in context
      const result = {
        summary: processedData,
        wasRefreshed: checkinResponse.data.passportRefreshed || false,
        isNewRecord: !checkinResponse.data.alreadyCheckedIn,
        metadata: {
          checkinTimestamp: new Date().toISOString(),
          eventName: checkinResponse.data.eventDetails?.event_name,
          location: checkinResponse.data.eventDetails?.location
        }
      };

      console.log('🎯 Processed check-in passport data:', result);
      return result;
      
    } catch (error) {
      console.error('💥 Error processing check-in passport data:', error);
      return null;
    }
  }

  /**
   * 🆕 NEW: Detect passport changes between before/after states
   */
  detectPassportChanges(beforeData, afterData) {
    if (!beforeData || !afterData) {
      return {
        isNewStamp: false,
        tierChanged: false,
        newTier: null,
        stampsGained: 0
      };
    }

    const stampsGained = (afterData.stamps || 0) - (beforeData.stamps || 0);
    const tierChanged = (beforeData.tier || 'new') !== (afterData.tier || 'new');
    
    return {
      isNewStamp: stampsGained > 0,
      tierChanged: tierChanged,
      newTier: tierChanged ? afterData.tier : null,
      stampsGained: stampsGained,
      previousTier: beforeData.tier || 'new',
      currentTier: afterData.tier || 'new'
    };
  }

  /**
   * Filter passport data based on feature flags
   */
  filterPassportData(data) {
    if (!data) {
      return {
        stamps: 0,
        yearly_stamps: 0,
        current_year: new Date().getFullYear(),
        tier: 'new',
        next_tier: { name: 'bronze', stamps_needed: 3 }
      };
    }
  
    const filtered = {
      stamps: data.stamps || 0,
      yearly_stamps: data.yearly_stamps || 0, // 🆕 NEW: Include yearly stamps
      current_year: data.current_year || new Date().getFullYear(), // 🆕 NEW
      tier: data.tier || 'new',
      next_tier: data.next_tier || null,
      // 🆕 CRITICAL: Always preserve member_status and subscribed_at
      member_status: data.member_status,
      subscribed_at: data.subscribed_at,
      email: data.email,
      signup_source: data.signup_source
    };
  
    // 🟢 ENABLED: Include full passport features
    if (isFeatureEnabled('showCityCount')) {
      filtered.cities = data.cities || 0;
    }
  
    if (isFeatureEnabled('showStreaks')) {
      filtered.current_streak = data.current_streak || 0;
    }
  
    if (isFeatureEnabled('showAchievements')) {
      filtered.achievements_count = data.achievements_count || 0;
    }
  
    // Include tier unlock detection
    if (data.tier_just_unlocked !== undefined) {
      filtered.tier_just_unlocked = data.tier_just_unlocked;
    }
  
    return filtered;
  }
  

  origfilterPassportData(data) {
    if (!data) {
      return {
        stamps: 0,
        tier: 'new',
        next_tier: { name: 'bronze', stamps_needed: 3 }
      };
    }

   
    const filtered = {
        stamps: data.stamps || 0,
        tier: data.tier || 'new',
        next_tier: data.next_tier || null,
        // 🆕 CRITICAL: Always preserve member_status and subscribed_at
        member_status: data.member_status,
        subscribed_at: data.subscribed_at,
        email: data.email,
        signup_source: data.signup_source
      };
    

    // 🟢 ENABLED: Include full passport features
    if (isFeatureEnabled('showCityCount')) {
      filtered.cities = data.cities || 0;
    }

    if (isFeatureEnabled('showStreaks')) {
      filtered.current_streak = data.current_streak || 0;
    }

    if (isFeatureEnabled('showAchievements')) {
      filtered.achievements_count = data.achievements_count || 0;
    }

    // Include tier unlock detection
    if (data.tier_just_unlocked !== undefined) {
      filtered.tier_just_unlocked = data.tier_just_unlocked;
    }

    return filtered;
  }


  /**
 * 🆕 NEW: Get yearly vs lifetime stats comparison
 */
async getYearlyStatsComparison(email) {
  try {
    if (!PASSPORT_CONFIG.enabled) {
      return { success: false, error: 'Passport system disabled' };
    }

    console.log('📊 Fetching yearly stats comparison for:', email);
    
    const { data, error } = await this.supabase.rpc('get_yearly_stats_comparison', {
      p_email: email.toLowerCase().trim()
    });

    if (error) {
      console.error('❌ Error fetching yearly stats:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ Yearly stats fetched:', data);
    return { success: true, data: data };
    
  } catch (error) {
    console.error('💥 Exception fetching yearly stats:', error);
    return { success: false, error: error.message };
  }
}




  /**
   * Format passport for header display (simplified)
   */
  formatForHeader(passportData) {
    if (!PASSPORT_CONFIG.enabled || !passportData) {
      return null;
    }
  
    const tierConfig = getTierConfig(passportData.tier);
    
    return {
      stamps: passportData.stamps || 0,
      yearly_stamps: passportData.yearly_stamps || 0, // 🆕 NEW
      current_year: passportData.current_year || new Date().getFullYear(), // 🆕 NEW
      tier: passportData.tier || 'new',
      icon: tierConfig.icon,
      tierName: tierConfig.name
    };
  }
  

  origformatForHeader(passportData) {
    if (!PASSPORT_CONFIG.enabled || !passportData) {
      return null;
    }

    const tierConfig = getTierConfig(passportData.tier);
    
    return {
      stamps: passportData.stamps || 0,
      tier: passportData.tier || 'new',
      icon: tierConfig.icon,
      tierName: tierConfig.name
    };
  }

  /**
   * Calculate progress to next tier (simplified)
   */
  calculateTierProgress(currentStamps, currentTier) {
    const origtierStamps = { bronze: 3, silver: 5, gold: 10 };
    const tierStamps = { bronze: 5, silver: 12, gold: 30, platinum: 60 }; 
    let nextTier = null;
    let stampsNeeded = 0;
    let progressPercentage = 0;

    switch (currentTier) {
      case 'new':
        nextTier = 'bronze';
        stampsNeeded = tierStamps.bronze - currentStamps;
        progressPercentage = (currentStamps / tierStamps.bronze) * 100;
        break;
      case 'bronze':
        nextTier = 'silver';
        stampsNeeded = tierStamps.silver - currentStamps;
        progressPercentage = ((currentStamps - tierStamps.bronze) / (tierStamps.silver - tierStamps.bronze)) * 100;
        break;
      case 'silver':
        nextTier = 'gold';
        stampsNeeded = tierStamps.gold - currentStamps;
        progressPercentage = ((currentStamps - tierStamps.silver) / (tierStamps.gold - tierStamps.silver)) * 100;
        break;
      case 'gold':
        nextTier = null; // Max tier reached
        stampsNeeded = 0;
        progressPercentage = 100;
        break;
    }

    return {
      nextTier,
      stampsNeeded: Math.max(0, stampsNeeded),
      progressPercentage: Math.min(100, Math.max(0, progressPercentage))
    };
  }

  /**
   * 🆕 NEW: Manually refresh passport (for edge cases)
   */
  async refreshPassport(email) {
    try {
      if (!PASSPORT_CONFIG.enabled) {
        return { success: false, error: 'Passport system disabled' };
      }

      console.log('🔄 Manually refreshing passport for:', email);
      
      const { data, error } = await this.supabase.rpc('refresh_member_passport', {
        p_email: email.toLowerCase().trim()
      });

      if (error) {
        console.error('❌ Error refreshing passport:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Passport refreshed successfully:', data);
      return { success: true, data: data };
      
    } catch (error) {
      console.error('💥 Exception refreshing passport:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🆕 NEW: Get tier configuration and benefits
   */
  getTierInfo(tier) {
    const tierConfig = getTierConfig(tier);
    const progress = this.calculateTierProgress(0, tier); // Will be overridden with actual stamps
    
    return {
      config: tierConfig,
      progress: progress,
      isMaxTier: tier === 'gold'
    };
  }

  /**
   * 🆕 NEW: Format passport for success display
   */
  /**
 * 🆕 ENHANCED: Format passport for success display with yearly tracking
 */
formatForSuccessDisplay(passportData, changes = null) {
  if (!PASSPORT_CONFIG.enabled || !passportData) {
    return null;
  }

  const tierConfig = getTierConfig(passportData.tier);
  const progress = this.calculateTierProgress(passportData.stamps, passportData.tier);
  
  return {
    stamps: passportData.stamps || 0,
    yearly_stamps: passportData.yearly_stamps || 0, // 🆕 NEW
    current_year: passportData.current_year || new Date().getFullYear(), // 🆕 NEW
    tier: passportData.tier || 'new',
    tierConfig: tierConfig,
    nextTier: progress.nextTier,
    stampsToNext: progress.stampsNeeded,
    progressPercentage: progress.progressPercentage,
    isMaxTier: passportData.tier === 'platinum',
    // Include change detection
    changes: changes || {
      isNewStamp: false,
      tierChanged: false,
      newTier: null,
      stampsGained: 0
    }
  };
}


// =====================================================
// 6. ADD: Helper to check if yearly reset is needed
// =====================================================

/**
 * 🆕 NEW: Check if user needs yearly reset (for client-side display)
 */
needsYearlyReset(passportData) {
  if (!passportData || !passportData.current_year) {
    return false;
  }
  
  const currentYear = new Date().getFullYear();
  return passportData.current_year < currentYear;
}

/**
 * 🆕 NEW: Get message about yearly reset
 */
getYearlyResetMessage(passportData) {
  const currentYear = new Date().getFullYear();
  const passportYear = passportData?.current_year || currentYear;
  
  if (passportYear < currentYear) {
    return {
      needsUpdate: true,
      message: `Your ${passportYear} stamps have been archived. Welcome to ${currentYear}! Your yearly stamps have reset to 0, but your lifetime stamps (${passportData?.stamps || 0}) are preserved forever.`,
      type: 'info'
    };
  }
  
  // Calculate days until next reset
  const daysUntilReset = Math.ceil(
    (new Date(`${currentYear + 1}-01-01`).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );
  
  return {
    needsUpdate: false,
    message: `Your ${currentYear} stamps reset in ${daysUntilReset} days. Current: ${passportData?.yearly_stamps || 0} stamps this year.`,
    type: 'upcoming',
    daysUntilReset
  };
}


  origfilterPassportDataformatForSuccessDisplay(passportData, changes = null) {
    if (!PASSPORT_CONFIG.enabled || !passportData) {
      return null;
    }

    const tierConfig = getTierConfig(passportData.tier);
    const progress = this.calculateTierProgress(passportData.stamps, passportData.tier);
    
    return {
      stamps: passportData.stamps || 0,
      tier: passportData.tier || 'new',
      tierConfig: tierConfig,
      nextTier: progress.nextTier,
      stampsToNext: progress.stampsNeeded,
      progressPercentage: progress.progressPercentage,
      isMaxTier: passportData.tier === 'gold',
      // Include change detection
      changes: changes || {
        isNewStamp: false,
        tierChanged: false,
        newTier: null,
        stampsGained: 0
      }
    };
  }

  /**
   * 🆕 NEW: Generate celebration messages
   */
  generateCelebrationMessage(changes, tierConfig) {
    if (!changes) return null;

    if (changes.tierChanged) {
      return {
        type: 'tier_unlock',
        title: '🎉 Tier Unlocked!',
        message: `You've reached ${tierConfig.name} status!`,
        icon: tierConfig.icon,
        priority: 'high'
      };
    }

    if (changes.isNewStamp) {
      return {
        type: 'new_stamp',
        title: 'New Stamp Earned! 🏷️',
        message: `Great job attending events! You now have ${changes.stampsGained > 1 ? `${changes.stampsGained} new stamps` : 'a new stamp'}.`,
        icon: '🏷️',
        priority: 'medium'
      };
    }

    return null;
  }

  // 🆕 NEW: Helper methods for member stats calculations

  /**
   * Calculate most frequent event type
   */
  calculateFavoriteEventType(eventData) {
    if (!eventData || eventData.length === 0) return null;
    
    const typeCounts = eventData.reduce((acc, event) => {
      acc[event.event_type] = (acc[event.event_type] || 0) + 1;
      return acc;
    }, {});
    
    return Object.keys(typeCounts).reduce((a, b) => 
      typeCounts[a] > typeCounts[b] ? a : b
    );
  }

  /**
   * Count events in current month
   */
  countEventsThisMonth(eventData) {
    if (!eventData || eventData.length === 0) return 0;
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return eventData.filter(event => {
      const eventDate = new Date(event.event_date);
      return eventDate.getMonth() === currentMonth && 
             eventDate.getFullYear() === currentYear;
    }).length;
  }

  /**
   * Count events in current year
   */
  countEventsThisYear(eventData) {
    if (!eventData || eventData.length === 0) return 0;
    
    const currentYear = new Date().getFullYear();
    
    return eventData.filter(event => {
      const eventDate = new Date(event.event_date);
      return eventDate.getFullYear() === currentYear;
    }).length;
  }
}

export default new PassportService();